"use client"

import { m } from "framer-motion"
import { cn } from "@/lib/utils"

export interface BarSegment {
  value: number
  /** Inline colour (e.g. an `rgba()` on a dark glass card). Mutually
   *  exclusive with `colorClassName`; prefer the token class where a DS
   *  utility exists so `lint-design-colors` stays happy. */
  color?: string
  /** DS colour-token utility (e.g. `bg-deep-green-500`). Takes precedence
   *  over `color`. */
  colorClassName?: string
  opacity?: number
  label?: string
}

export interface SegmentedBarProps {
  segments: BarSegment[]
  height?: number
  gap?: number
  /** Animate each segment's width in on mount. Default true; pass false to
   *  render a static bar (matches the pre-existing membership overlays). */
  animate?: boolean
  className?: string
}

export function SegmentedBar({
  segments,
  height = 6,
  gap = 3,
  animate = true,
  className,
}: SegmentedBarProps) {
  const total = segments.reduce((sum, s) => sum + s.value, 0)
  if (total === 0) return null

  return (
    <div className={cn("flex w-full", className)} style={{ height, gap }}>
      {segments.map((segment, i) => {
        const widthPercent = (segment.value / total) * 100
        const style = {
          backgroundColor: segment.colorClassName ? undefined : segment.color,
          opacity: segment.opacity ?? 1,
          minWidth: 8,
          flexShrink: 0,
        }
        const segmentClassName = cn("rounded-full", segment.colorClassName)

        if (!animate) {
          return (
            <div
              key={i}
              title={segment.label}
              style={{ ...style, width: `${widthPercent}%` }}
              className={segmentClassName}
            />
          )
        }

        return (
          <m.div
            key={i}
            initial={{ width: 0 }}
            animate={{ width: `${widthPercent}%` }}
            transition={{
              duration: 0.8,
              ease: [0.16, 1, 0.3, 1],
              delay: 0.2 + i * 0.12,
            }}
            title={segment.label}
            style={style}
            className={segmentClassName}
          />
        )
      })}
    </div>
  )
}
